import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Plane, Hotel, Utensils, Gift, Sparkles, Check, Star, ChevronDown, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { FAVICON } from '../assets';
import './landing-vip.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const RewardsPage = () => {
  const { i18n } = useTranslation();
  const isSpanish = i18n.language?.startsWith('es');
  const lang = i18n.language?.substring(0, 2) || 'es';
  
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const [rewards, setRewards] = useState([]);
  const [userRewards, setUserRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReward, setSelectedReward] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [userPoints, setUserPoints] = useState(user?.points || 0);
  const [openFaq, setOpenFaq] = useState(null);

  // Content translations
  const content = {
    es: {
      heroEyebrow: "Catálogo de Recompensas",
      heroTitle: <>Transforma tu lealtad<br/>en experiencias inolvidables</>,
      heroAccent: "Cada punto te acerca a tu próximo destino.",
      heroSub: <>Has acumulado valor con cada compra local. Ahora es momento de <strong>convertirlo en recuerdos</strong>. Explora nuestro catálogo y elige tu recompensa.</>,
      
      pointsLabel: "Tus puntos disponibles",
      
      howItWorksTitle: "¿Cómo funcionan las recompensas?",
      steps: [
        { icon: Gift, title: "Acumula Puntos", desc: "Cada euro gastado en comercios locales = 1 punto" },
        { icon: Sparkles, title: "Elige tu Destino", desc: "Explora nuestro catálogo de experiencias premium" },
        { icon: Plane, title: "Disfruta el Viaje", desc: "Canjea tus puntos y vive momentos únicos" }
      ],
      
      catalogTitle: <>Elige tu próxima<br/>aventura</>,
      catalogSub: "Hoteles de 4 y 5 estrellas con pensión completa. Tú eliges cómo llegar.",
      
      pointsNeeded: "puntos necesarios",
      hotelIncluded: "Hotel con pensión completa incluido",
      flightsNote: "Vuelos no incluidos",
      redeemBtn: "Canjear Recompensa",
      loginRequired: "Inicia sesión para canjear",
      notEnoughPoints: "Puntos insuficientes",
      
      redeemedTitle: "Tus Recompensas Canjeadas",
      redeemedOn: "Canjeado el",
      pointsUsed: "puntos utilizados",
      statusCompleted: "Completado",
      statusConfirmed: "Confirmado",
      statusPending: "Pendiente",
      
      faqTitle: <>Preguntas sobre<br/>las recompensas</>,
      faqs: [
        { q: "¿Qué incluyen exactamente las recompensas?", a: "Nuestras recompensas incluyen estancias con pensión completa (alojamiento, desayuno, almuerzo y cena) en hoteles de 4 y 5 estrellas cuidadosamente seleccionados. El transporte al destino corre por tu cuenta, dándote total flexibilidad." },
        { q: "¿Mis puntos caducan?", a: "No. Tus puntos son tuyos para siempre. Acumula a tu ritmo y canjea cuando tú decidas." },
        { q: "¿Cómo se verifica el canje?", a: "Una vez confirmes el canje, recibirás un email con los detalles de tu reserva. Nuestro equipo te contactará en 48h para coordinar las fechas." },
        { q: "¿Puedo cancelar un canje?", a: "Los canjes son definitivos. Asegúrate de tener disponibilidad antes de confirmar. En casos excepcionales, contacta con soporte." }
      ],
      
      ctaTitle: <>¿Aún no tienes<br/>suficientes puntos?</>,
      ctaSub: "Sigue comprando en tus comercios favoritos y acumula puntos con cada visita.",
      ctaBtn: "Descubrir Comercios",
      
      dialogTitle: "Confirmar canje de recompensa",
      dialogDesc: "Vas a canjear",
      dialogPoints: "por",
      dialogRemaining: "Te quedarán",
      dialogWarning: "Esta acción no se puede deshacer.",
      dialogCancel: "Cancelar",
      dialogConfirm: "Sí, Canjear",
      dialogRedeeming: "Procesando...",
    },
    en: {
      heroEyebrow: "Rewards Catalogue",
      heroTitle: <>Transform your loyalty<br/>into unforgettable experiences</>,
      heroAccent: "Every point brings you closer to your next destination.",
      heroSub: <>You've accumulated value with every local purchase. Now it's time to <strong>turn it into memories</strong>. Explore our catalogue and choose your reward.</>,
      
      pointsLabel: "Your available points",
      
      howItWorksTitle: "How do rewards work?",
      steps: [
        { icon: Gift, title: "Earn Points", desc: "Every euro spent at local shops = 1 point" },
        { icon: Sparkles, title: "Choose your Destination", desc: "Explore our catalogue of premium experiences" },
        { icon: Plane, title: "Enjoy the Journey", desc: "Redeem your points and live unique moments" }
      ],
      
      catalogTitle: <>Choose your next<br/>adventure</>,
      catalogSub: "4 and 5-star hotels with full board. You choose how to get there.",
      
      pointsNeeded: "points required",
      hotelIncluded: "Hotel with full board included",
      flightsNote: "Flights not included",
      redeemBtn: "Redeem Reward",
      loginRequired: "Log in to redeem",
      notEnoughPoints: "Insufficient points",
      
      redeemedTitle: "Your Redeemed Rewards",
      redeemedOn: "Redeemed on",
      pointsUsed: "points used",
      statusCompleted: "Completed",
      statusConfirmed: "Confirmed",
      statusPending: "Pending",
      
      faqTitle: <>Questions about<br/>rewards</>,
      faqs: [
        { q: "What exactly do the rewards include?", a: "Our rewards include full-board stays (accommodation, breakfast, lunch and dinner) at carefully selected 4 and 5-star hotels. Transport to the destination is on you, giving you total flexibility." },
        { q: "Do my points expire?", a: "No. Your points are yours forever. Accumulate at your own pace and redeem whenever you decide." },
        { q: "How is the redemption verified?", a: "Once you confirm the redemption, you'll receive an email with your booking details. Our team will contact you within 48h to coordinate dates." },
        { q: "Can I cancel a redemption?", a: "Redemptions are final. Make sure you have availability before confirming. In exceptional cases, contact support." }
      ],
      
      ctaTitle: <>Don't have enough<br/>points yet?</>,
      ctaSub: "Keep shopping at your favourite local businesses and earn points with every visit.",
      ctaBtn: "Discover Businesses",
      
      dialogTitle: "Confirm reward redemption",
      dialogDesc: "You're about to redeem",
      dialogPoints: "for",
      dialogRemaining: "You'll have",
      dialogWarning: "This action cannot be undone.",
      dialogCancel: "Cancel",
      dialogConfirm: "Yes, Redeem",
      dialogRedeeming: "Processing...",
    }
  };

  const c = isSpanish ? content.es : content.en;

  useEffect(() => {
    fetchRewards();
    if (user) {
      fetchUserRewards();
      fetchUserPoints();
    }
  }, []);

  const fetchRewards = async () => {
    try {
      const response = await axios.get(`${API}/rewards/catalog`);
      setRewards(response.data);
    } catch (error) {
      console.error('Error fetching rewards:', error);
      toast.error(isSpanish ? 'Error al cargar recompensas' : 'Error loading rewards');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserRewards = async () => {
    try {
      const response = await axios.get(`${API}/rewards/user/${user.id}`);
      setUserRewards(response.data);
    } catch (error) {
      console.error('Error fetching user rewards:', error);
    }
  };

  const fetchUserPoints = async () => {
    try {
      const response = await axios.get(`${API}/users/${user.id}`);
      setUserPoints(response.data.points);
      const updatedUser = { ...user, points: response.data.points };
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (error) {
      console.error('Error fetching user points:', error);
    }
  };

  const handleRedeemClick = (reward) => {
    if (!user) {
      toast.error(isSpanish ? 'Debes iniciar sesión para canjear recompensas' : 'You must log in to redeem rewards');
      return;
    }
    
    if (userPoints < reward.pointsCost) {
      toast.error(isSpanish ? 'No tienes suficientes puntos' : 'You don\'t have enough points');
      return;
    }
    
    setSelectedReward(reward);
    setDialogOpen(true);
  };

  const confirmRedeem = async () => {
    if (!selectedReward) return;
    
    setRedeeming(true);
    try {
      const response = await axios.post(
        `${API}/rewards/redeem`,
        { rewardId: selectedReward.id },
        { headers: { 'X-User-Id': user.id } }
      );
      
      setUserPoints(response.data.remainingPoints);
      toast.success(isSpanish ? '¡Recompensa canjeada exitosamente!' : 'Reward redeemed successfully!');
      setDialogOpen(false);
      fetchUserRewards();
      
      const updatedUser = { ...user, points: response.data.remainingPoints };
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (error) {
      console.error('Error redeeming reward:', error);
      toast.error(error.response?.data?.detail || (isSpanish ? 'Error al canjear recompensa' : 'Error redeeming reward'));
    } finally {
      setRedeeming(false);
    }
  };

  const getRewardImage = (rewardId) => {
    if (rewardId.includes('paris')) return "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=600&q=80";
    if (rewardId.includes('canarias') || rewardId.includes('canary')) return "https://images.unsplash.com/photo-1590523741831-ab7e8b8f9c7f?w=600&q=80";
    if (rewardId.includes('europa') || rewardId.includes('europe')) return "https://images.unsplash.com/photo-1499856871958-5b9627545d1a?w=600&q=80";
    if (rewardId.includes('dinner') || rewardId.includes('cena')) return "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600&q=80";
    return "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&q=80";
  };

  const getRewardTag = (rewardId) => {
    if (rewardId.includes('paris')) return isSpanish ? "El más solicitado" : "Most requested";
    if (rewardId.includes('canarias') || rewardId.includes('canary')) return isSpanish ? "Paraíso cercano" : "Paradise nearby";
    if (rewardId.includes('europa') || rewardId.includes('europe')) return isSpanish ? "Experiencia definitiva" : "Ultimate experience";
    return isSpanish ? "Premium" : "Premium";
  };

  return (
    <div className="vip-landing">
      {/* Hero Section */}
      <section className="vip-hero" style={{ minHeight: 'auto', padding: '140px 24px 100px' }}>
        <div className="vip-hero-bg">
          <div className="vip-hero-gradient" />
          <div className="vip-hero-pattern" />
        </div>
        
        <div className="vip-hero-content">
          <motion.div 
            className="vip-eyebrow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Star className="vip-eyebrow-icon" />
            {c.heroEyebrow}
          </motion.div>
          
          <motion.h1 
            className="vip-hero-title"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            {c.heroTitle}
          </motion.h1>
          
          <motion.p 
            className="vip-hero-accent"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            {c.heroAccent}
          </motion.p>
          
          <motion.p 
            className="vip-hero-sub"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            {c.heroSub}
          </motion.p>
          
          {user && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="vip-points-badge"
              style={{
                marginTop: '40px',
                display: 'inline-block',
                background: 'rgba(201, 169, 98, 0.15)',
                border: '1px solid rgba(201, 169, 98, 0.3)',
                borderRadius: '12px',
                padding: '24px 48px'
              }}
            >
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '8px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                {c.pointsLabel}
              </p>
              <p style={{ fontFamily: "'Playfair Display', serif", fontSize: '48px', fontWeight: '700', color: '#c9a962' }} data-testid="user-available-points">
                {userPoints.toLocaleString()}
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section className="vip-counter" style={{ padding: '80px 24px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{ 
            fontFamily: "'Playfair Display', serif", 
            fontSize: 'clamp(1.5rem, 3vw, 2rem)', 
            color: '#0a1628', 
            textAlign: 'center',
            marginBottom: '48px'
          }}>
            {c.howItWorksTitle}
          </h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '32px' }}>
            {c.steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                style={{
                  textAlign: 'center',
                  padding: '32px 24px',
                  background: '#f8f6f1',
                  borderRadius: '12px',
                  border: '1px solid #e8e4db'
                }}
              >
                <div style={{
                  width: '64px',
                  height: '64px',
                  margin: '0 auto 20px',
                  background: 'linear-gradient(135deg, #0a1628 0%, #1a2d4a 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <step.icon style={{ width: '28px', height: '28px', color: '#c9a962' }} />
                </div>
                <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '20px', color: '#0a1628', marginBottom: '8px' }}>
                  {step.title}
                </h3>
                <p style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rewards Catalog */}
      <section className="vip-rewards" style={{ padding: '120px 24px' }}>
        <div className="vip-section-inner">
          <span className="vip-eyebrow-small vip-center vip-gold">{c.heroEyebrow}</span>
          <h2 className="vip-section-title vip-center vip-white">{c.catalogTitle}</h2>
          <p className="vip-section-sub vip-center" style={{ color: 'rgba(255,255,255,0.7)' }}>{c.catalogSub}</p>
          
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '3px solid rgba(201, 169, 98, 0.2)',
                borderTopColor: '#c9a962',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            </div>
          ) : (
            <div className="vip-rewards-grid">
              {rewards.map((reward, idx) => {
                const canAfford = user && userPoints >= reward.pointsCost;
                
                return (
                  <motion.div
                    key={reward.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                    viewport={{ once: true }}
                    className="vip-reward-card"
                    data-testid={`reward-card-${reward.id}`}
                  >
                    <div className="vip-reward-image">
                      <img src={getRewardImage(reward.id)} alt={reward.name} />
                      <span className="vip-reward-tag">{getRewardTag(reward.id)}</span>
                    </div>
                    <div className="vip-reward-content">
                      <div style={{ 
                        fontFamily: "'Playfair Display', serif",
                        fontSize: '36px', 
                        fontWeight: '700', 
                        color: '#c9a962',
                        marginBottom: '4px'
                      }}>
                        {reward.pointsCost.toLocaleString()}
                      </div>
                      <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginBottom: '16px', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                        {c.pointsNeeded}
                      </p>
                      
                      <h3 className="vip-reward-destination">{reward.name}</h3>
                      <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.6)', marginBottom: '16px', lineHeight: '1.5' }}>
                        {reward.description}
                      </p>
                      
                      <div style={{ 
                        padding: '12px 0', 
                        borderTop: '1px solid rgba(255,255,255,0.1)',
                        marginBottom: '16px',
                        fontSize: '12px',
                        color: 'rgba(255,255,255,0.5)'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                          <Check style={{ width: '14px', height: '14px', color: '#c9a962' }} />
                          {c.hotelIncluded}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Plane style={{ width: '14px', height: '14px', color: 'rgba(255,255,255,0.3)' }} />
                          {c.flightsNote}
                        </div>
                      </div>
                      
                      <button
                        onClick={() => handleRedeemClick(reward)}
                        disabled={!user || !canAfford}
                        data-testid={`redeem-${reward.id}`}
                        style={{
                          width: '100%',
                          padding: '14px 24px',
                          background: (!user || !canAfford) 
                            ? 'rgba(255,255,255,0.1)' 
                            : 'linear-gradient(135deg, #c9a962 0%, #a88b3d 100%)',
                          color: (!user || !canAfford) ? 'rgba(255,255,255,0.4)' : '#0a1628',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: (!user || !canAfford) ? 'not-allowed' : 'pointer',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        {!user ? c.loginRequired : canAfford ? c.redeemBtn : c.notEnoughPoints}
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* User Redeemed Rewards */}
      {user && userRewards.length > 0 && (
        <section style={{ padding: '100px 24px', background: '#f8f6f1' }}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            <h2 style={{ 
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              color: '#0a1628',
              marginBottom: '40px'
            }}>
              {c.redeemedTitle}
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }} data-testid="redeemed-rewards-list">
              {userRewards.map((reward) => (
                <motion.div
                  key={reward.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    background: '#fff',
                    borderRadius: '12px',
                    padding: '24px',
                    border: '1px solid #e8e4db',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '16px',
                    flexWrap: 'wrap'
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <Check style={{ width: '20px', height: '20px', color: '#22c55e' }} />
                      <h3 style={{ fontFamily: "'Playfair Display', serif", fontSize: '18px', color: '#0a1628' }}>
                        {reward.rewardName}
                      </h3>
                    </div>
                    <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '4px' }}>
                      {c.redeemedOn} {new Date(reward.redeemedAt).toLocaleDateString(isSpanish ? 'es-ES' : 'en-US', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                    <p style={{ fontSize: '14px', color: '#a88b3d', fontWeight: '500' }}>
                      {reward.pointsCost.toLocaleString()} {c.pointsUsed}
                    </p>
                  </div>
                  <span style={{
                    padding: '6px 14px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600',
                    background: reward.status === 'completed' ? '#dcfce7' : reward.status === 'confirmed' ? '#dbeafe' : '#fef3c7',
                    color: reward.status === 'completed' ? '#166534' : reward.status === 'confirmed' ? '#1e40af' : '#92400e'
                  }}>
                    {reward.status === 'completed' ? c.statusCompleted :
                     reward.status === 'confirmed' ? c.statusConfirmed : c.statusPending}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      <section className="vip-faq" style={{ background: '#fff' }}>
        <div className="vip-section-inner">
          <span className="vip-eyebrow-small">FAQ</span>
          <h2 className="vip-section-title">{c.faqTitle}</h2>
          
          <div className="vip-faq-list">
            {c.faqs.map((faq, i) => (
              <div 
                key={i} 
                className={`vip-faq-item ${openFaq === i ? 'open' : ''}`}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <div className="vip-faq-q">
                  <span>{faq.q}</span>
                  <ChevronDown className={`vip-faq-chevron ${openFaq === i ? 'rotate' : ''}`} />
                </div>
                {openFaq === i && (
                  <motion.div 
                    className="vip-faq-a"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    {faq.a}
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="vip-final" style={{ padding: '120px 24px' }}>
        <div className="vip-final-bg" />
        <div className="vip-final-content">
          <span className="vip-eyebrow-small vip-gold">{isSpanish ? 'Sigue Acumulando' : 'Keep Earning'}</span>
          <h2 className="vip-final-title">{c.ctaTitle}</h2>
          <p className="vip-final-sub">{c.ctaSub}</p>
          <Link to="/directory" className="vip-btn-final" data-testid="rewards-discover-cta">
            {c.ctaBtn}
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ background: '#0a1628', borderTop: '1px solid rgba(255,255,255,0.1)', padding: '32px 24px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img src={FAVICON} alt="LokAlGrow" style={{ width: '32px', height: '32px', borderRadius: '8px' }} />
            <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>© {new Date().getFullYear()} LokAlGrow</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', fontSize: '14px' }}>
            <Link to="/privacy" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none', transition: 'color 0.2s' }}>
              {isSpanish ? 'Privacidad' : 'Privacy'}
            </Link>
            <Link to="/terms" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none', transition: 'color 0.2s' }}>
              {isSpanish ? 'Términos' : 'Terms'}
            </Link>
            <Link to="/cookies" style={{ color: 'rgba(255,255,255,0.5)', textDecoration: 'none', transition: 'color 0.2s' }}>
              Cookies
            </Link>
          </div>
        </div>
      </footer>

      {/* Confirmation Dialog */}
      <AlertDialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <AlertDialogContent style={{ background: '#fff', border: '1px solid #e8e4db' }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ fontFamily: "'Playfair Display', serif", color: '#0a1628' }}>
              {c.dialogTitle}
            </AlertDialogTitle>
            <AlertDialogDescription style={{ color: '#6b7280' }}>
              {c.dialogDesc} <strong style={{ color: '#0a1628' }}>{selectedReward?.name}</strong> {c.dialogPoints}{' '}
              <strong style={{ color: '#a88b3d' }}>{selectedReward?.pointsCost?.toLocaleString()} puntos</strong>.
              <br /><br />
              {c.dialogRemaining} <strong style={{ color: '#0a1628' }}>{(userPoints - (selectedReward?.pointsCost || 0)).toLocaleString()} puntos</strong>.
              <br /><br />
              <span style={{ color: '#dc2626' }}>{c.dialogWarning}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={redeeming} style={{ border: '1px solid #e8e4db' }}>
              {c.dialogCancel}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRedeem}
              disabled={redeeming}
              data-testid="confirm-redeem-button"
              style={{
                background: 'linear-gradient(135deg, #c9a962 0%, #a88b3d 100%)',
                color: '#0a1628'
              }}
            >
              {redeeming ? c.dialogRedeeming : c.dialogConfirm}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default RewardsPage;
